import * as React from 'react';
import Axios from 'axios';
// TODO: APP-3570 Remove when rets2 decodes
import { decode } from 'html-entities';

import errorReporter from '@app/errorReporter';
import type { NonAgentJsonType } from '@bb/domain/dist/types';
import { AuthContextUser } from 'contexts/types';
import Pagination from './Pagination';
import searchUsers from '../../api/UserController/search';

// Both agents and non-agents are fetched in this component in separate routes but type guards or type narrowing isn't used to differentiate
// These fields are added to avoid that heavy refactor
type UndefinedNonAgentFields = {
  isCustomAgent?: undefined;
  addressName?: undefined;
};
type UndefinedAgentFields = { type?: undefined; companyName?: undefined };

type NonAgentUserResult = {
  id: string;
  score: number;
  fullName: string;
} & Pick<NonAgentJsonType, 'firstName' | 'lastName' | 'companyName' | 'type'> &
  UndefinedNonAgentFields;

// This can be further divided into the 3 agent types: Roster agent, Custom agent, and BrokerBay agent
export type AgentUserResult = {
  id?: string;
  score: number;
  firstName: string;
  lastName: string;
  orgName: string;
  addressName: string;
  email: string;
  fullName: string;
  isCustomAgent?: boolean;
  uid?: string;
  boardId?: string;
  mlsType?: string;
} & UndefinedAgentFields;

type UserResult = AgentUserResult | NonAgentUserResult;

type AgentRosterInputState = {
  loading: boolean;
  cancelSearch: boolean;
  searchUserResults?: UserResult[];
  searchUserPopups: UserResult[];
  agentSuggestName: string;
  selectedUserIndex: number;
  page: number;
  searchUserResultsCount?: number;
  isBookShowing?: boolean;
  isMobile?: boolean;
  includeCustomAgents?: boolean;
};

type AgentRosterInputProps = {
  selectUser: (item: UserResult) => void;
  selectCustomAgent?: (item: UserResult) => void;
  deselectUser: () => void;
  filterSelf?: boolean;
  auth: AuthContextUser;
  nonAgent?: boolean;
  listingId: string;
  isBookShowing?: boolean;
  isMobile?: boolean;
  includeCustomAgents?: boolean;
};

class AgentRosterInput extends React.Component<
  AgentRosterInputProps,
  AgentRosterInputState
> {
  constructor(props: AgentRosterInputProps) {
    super(props);
    this.state = {
      loading: false,
      cancelSearch: false,
      searchUserResults: undefined,
      searchUserPopups: [],
      agentSuggestName: '',
      selectedUserIndex: -1,
      page: 1,
      searchUserResultsCount: -1,
      isBookShowing: props.isBookShowing,
      isMobile: props.isMobile,
      includeCustomAgents: props.includeCustomAgents,
    };
  }

  _handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { agentSuggestName, selectedUserIndex, searchUserPopups } =
      this.state;
    const { listingId } = this.props;
    const total = searchUserPopups.length || 1;
    if (event.key === 'Enter' && selectedUserIndex === -1) {
      if (!agentSuggestName) return;
      this.search(agentSuggestName, listingId);
    } else if (event.key === 'Enter' && selectedUserIndex > -1) {
      this.onAgentSelect(searchUserPopups[selectedUserIndex]);
    } else if (['ArrowUp', 'Up'].includes(event.key)) {
      const newIndex =
        selectedUserIndex === -1
          ? total - 1
          : (selectedUserIndex + total - 1) % total;
      this.setState({ selectedUserIndex: newIndex });
    } else if (['ArrowDown', 'Down'].includes(event.key)) {
      const newIndex = (selectedUserIndex + 1) % total;
      this.setState({ selectedUserIndex: newIndex });
    } else if (['Escape', 'Esc'].includes(event.key)) {
      this.setState({ searchUserPopups: [], selectedUserIndex: -1 });
    }
  };

  public onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const { currentTarget } = event;

    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        this.setState({ searchUserPopups: [], selectedUserIndex: -1 });
      }
    }, 0);
  };

  public select = (user: UserResult) => {
    const { selectUser, selectCustomAgent } = this.props;
    const isCustomAgentSearch = user?.isCustomAgent && selectCustomAgent;
    this.setState({ searchUserResults: undefined }, () =>
      isCustomAgentSearch ? selectCustomAgent(user) : selectUser(user),
    );
  };

  public search = async (name: string, listingId: string) => {
    try {
      const { deselectUser, nonAgent, includeCustomAgents } = this.props;
      const { page } = this.state;
      this.setState({
        loading: true,
        cancelSearch: true,
        searchUserPopups: [],
      });
      deselectUser();

      let users: UserResult[];
      let count;
      if (nonAgent) {
        const { data } = await Axios.get<UserResult[]>(`nonagent/search`, {
          params: { name },
        });
        users = data;
      } else {
        const data = await searchUsers({
          name,
          page,
          listingId,
          excludeInternalOrgs: false,
          ...(includeCustomAgents ? { includeCustomAgents } : {}),
        });
        ({ users, count } = data);
      }
      this.setState({
        searchUserResultsCount: count,
        searchUserResults: users.sort((a, b) => b.score - a.score),
      });
    } catch (err) {
      errorReporter.error(`Error searching roster: ${err}`);
    } finally {
      this.setState({ loading: false });
    }
  };

  public agentSuggest = async (name: string) => {
    const { cancelSearch, agentSuggestName } = this.state;
    const { auth, includeCustomAgents, nonAgent, listingId } = this.props;

    if (agentSuggestName !== name) return;
    if (!name) {
      this.setState({ searchUserPopups: [] });
      return;
    }
    if (cancelSearch) {
      this.setState({ searchUserPopups: [], cancelSearch: false });
      return;
    }
    try {
      const { filterSelf } = this.props;
      let users: UserResult[];
      if (nonAgent) {
        const { data } = await Axios.get<NonAgentUserResult[]>(
          `nonagent/search`,
          { params: { name } },
        );
        users = data;
      } else {
        const data = await searchUsers({
          name,
          listingId,
          excludeInternalOrgs: false,
          ...(includeCustomAgents ? { includeCustomAgents } : {}),
        });
        ({ users } = data);
      }
      const searchUserPopups = filterSelf
        ? users
            .filter(({ id }) => id !== auth.id)
            .sort((a, b) => b.score - a.score)
            .slice(0, 10)
        : users.sort((a, b) => b.score - a.score).slice(0, 10);

      // prevents race condition where enter is pressed before this call finishes
      const { cancelSearch: stillCancelSearch } = this.state;
      if (stillCancelSearch) {
        return;
      }
      this.setState({
        searchUserPopups,
        searchUserResultsCount: searchUserPopups.length,
      });
    } catch (err) {
      errorReporter.error(`Error searching roster: ${err}`);
    } finally {
      this.setState({ cancelSearch: false });
    }
  };

  public onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const agentSuggestName = event.target.value;
    this.setState(
      {
        agentSuggestName,
        page: 1,
        searchUserResults: undefined,
        searchUserResultsCount: -1,
      },
      () => {
        setTimeout(() => {
          this.agentSuggest(agentSuggestName);
        }, 300);
      },
    );
  };

  public onAgentSelect = (user: UserResult) => {
    this.setState(
      { agentSuggestName: '', searchUserPopups: [], selectedUserIndex: -1 },
      () => this.select(user),
    );
  };

  public onPageChange = (page: number) => {
    const { agentSuggestName } = this.state;
    const { listingId } = this.props;
    this.setState({ page }, () => this.search(agentSuggestName, listingId));
  };

  render() {
    const {
      agentSuggestName,
      searchUserResults,
      searchUserPopups,
      selectedUserIndex,
      page,
      searchUserResultsCount,
      loading,
    } = this.state;
    const { nonAgent } = this.props;
    return (
      <div style={{ position: 'relative' }}>
        <div className="form-group" onBlur={this.onBlur} tabIndex={1}>
          <input
            autoFocus
            type="text"
            className="form-control"
            id="agent-roster-input"
            value={agentSuggestName}
            placeholder="Name"
            onChange={this.onSearchChange}
            onKeyDown={this._handleKeyDown}
            data-cy={nonAgent ? 'non-agent-name' : 'agent-name'}
            autoComplete="new-password"
          />

          {searchUserPopups.length > 0 && (
            <ul
              className="dropdown-menu"
              style={{
                top: '38px',
                left: '0',
                width: '100%',
                display: 'block',
                ...(this.state.isBookShowing
                  ? {
                      overflowY: 'auto',
                      ...(this.state.isMobile ? { height: '20vh' } : 0),
                    }
                  : 0),
              }}
              role="listbox"
            >
              {[...searchUserPopups].map((user, index) => (
                <li
                  key={index}
                  onClick={() => this.onAgentSelect(user)}
                  role="option"
                  className={index === selectedUserIndex ? 'active' : ''}
                >
                  <a data-cy={`${user.id}`} style={{ whiteSpace: 'normal' }}>
                    <strong>
                      <span>{`${decode(user.firstName)} ${decode(
                        user.lastName,
                      )}`}</span>
                    </strong>
                    <br />
                    <span>{decode(user.addressName || user.companyName)}</span>
                    {user.type && (
                      <>
                        <br /> <span>{user.type}</span>
                      </>
                    )}
                    {user.isCustomAgent && (
                      <>
                        <br /> <span>Custom Agent</span>
                      </>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
        {searchUserResults && searchUserResults.length > 0 && (
          <div>
            {[...searchUserResults].map((user, index) => (
              <div key={index} style={{ marginBottom: '10px' }}>
                <a
                  onClick={() => this.select(user)}
                  className="agent-list-link"
                >
                  <strong>
                    {user.firstName} {user.lastName}
                  </strong>
                  <br /> {user.addressName || user.companyName}
                  {user.type && (
                    <>
                      <br /> <span>{user.type}</span>
                    </>
                  )}
                  {user.isCustomAgent && (
                    <>
                      <br /> <span>Custom Agent</span>
                    </>
                  )}
                </a>
              </div>
            ))}
            {!nonAgent && searchUserResultsCount! > 10 && (
              <Pagination
                totalItems={Math.min(100, searchUserResultsCount!)}
                itemsPerPage={10}
                maxSize={5}
                currentPage={page}
                onChange={this.onPageChange}
                disabled={loading}
              />
            )}
          </div>
        )}
        {searchUserResultsCount === 0 && (
          <div>
            <label>No users found.</label>
          </div>
        )}
      </div>
    );
  }
}

export default AgentRosterInput;
